import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../hooks/useAuth';
import { fetchWithAuth } from '../util/fetchWithAuth';

interface AskResponse {
  answer?: string;
  queries?: string[];
  errors?: string[];
}

const EXAMPLE_QUESTIONS = [
  'How many customers do we have?',
  'How many orders are in each status right now?',
  'How much delivery income did we make this month?',
  'New vs returning customer orders this month',
];

// Optional focus nudges. Keys must match the backend FOCUS_HINTS keys. `financesOnly` areas are
// hidden from managers, who can't query the bank/external transaction tables — the backend hides
// those from the assistant's tools regardless, this just keeps the UI honest about it.
const FOCUS_AREAS = [
  { key: 'orders', label: 'Orders & sales', financesOnly: false },
  { key: 'finances', label: 'Bank & financial transactions', financesOnly: true },
];

// Style the markdown output without relying on the typography plugin (not installed).
// `node` is react-markdown's AST node — strip it so it isn't spread onto the DOM element.
/* eslint-disable @typescript-eslint/no-explicit-any */
const markdownComponents = {
  p: ({ node, ...props }: any) => <p className="mb-2 leading-relaxed" {...props} />,
  ul: ({ node, ...props }: any) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
  ol: ({ node, ...props }: any) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
  strong: ({ node, ...props }: any) => <strong className="font-semibold" {...props} />,
  // Pass children explicitly so jsx-a11y can see these anchors/headings have content
  // (it can't tell statically that a spread `{...props}` carries `children`).
  a: ({ node, children, ...props }: any) => (
    <a className="text-blue-600 hover:underline" {...props}>{children}</a>
  ),
  h1: ({ node, children, ...props }: any) => (
    <h2 className="text-lg font-bold mt-3 mb-2" {...props}>{children}</h2>
  ),
  h2: ({ node, children, ...props }: any) => (
    <h3 className="text-base font-bold mt-3 mb-1" {...props}>{children}</h3>
  ),
  h3: ({ node, children, ...props }: any) => (
    <h4 className="font-semibold mt-2 mb-1" {...props}>{children}</h4>
  ),
  code: ({ node, ...props }: any) => <code className="bg-gray-100 rounded px-1 py-0.5 text-sm" {...props} />,
  table: ({ node, ...props }: any) => (
    <div className="overflow-x-auto my-2">
      <table className="min-w-full text-sm border border-gray-200" {...props} />
    </div>
  ),
  thead: ({ node, ...props }: any) => <thead className="bg-gray-50" {...props} />,
  th: ({ node, ...props }: any) => <th className="border border-gray-200 px-3 py-1.5 text-left font-semibold" {...props} />,
  td: ({ node, ...props }: any) => <td className="border border-gray-200 px-3 py-1.5" {...props} />,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const Assistant: React.FC = () => {
  const { user } = useAuth();
  const canViewFinances = user?.role === 'admin';
  const focusAreaOptions = FOCUS_AREAS.filter((area) => canViewFinances || !area.financesOnly);
  const [question, setQuestion] = useState('');
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [answer, setAnswer] = useState<string | null>(null);
  const [queries, setQueries] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleFocus = (key: string) => {
    setFocusAreas((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    );
  };

  const ask = async (rawQuestion: string) => {
    const trimmed = rawQuestion.trim();
    if (!trimmed || isLoading) return;

    setIsLoading(true);
    setError(null);
    setAnswer(null);
    setQueries([]);

    try {
      const res = await fetchWithAuth('/data_questions', {
        method: 'POST',
        body: JSON.stringify({ question: trimmed, focus_areas: focusAreas }),
      });
      const data: AskResponse = await res.json();

      if (!res.ok) {
        setError(data.errors?.[0] ?? 'Something went wrong. Please try again.');
        return;
      }

      setAnswer(data.answer ?? '');
      setQueries(data.queries ?? []);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    ask(question);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      ask(question);
    }
  };

  return (
    <div className="px-4 md:px-6 py-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800">Assistant</h1>
      <p className="text-gray-500 mt-1 mb-4 text-sm">
        Ask a question about Tini Express data. Queries are strictly read-only.
        {!canViewFinances && ' Bank and external transaction data is not available to your account.'}
      </p>

      <form onSubmit={handleSubmit}>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. How many orders were placed this month?"
          rows={3}
          className="w-full border border-gray-200 rounded px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-800"
        />

        {/* The focus toggles exist to disambiguate between data sources, so they only earn their
            place when there's more than one to choose from. A manager has only orders — a lone
            checkbox would offer a choice that isn't one. */}
        {focusAreaOptions.length > 1 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-xs text-gray-400">Focus (optional):</span>
            {focusAreaOptions.map((area) => (
              <label key={area.key} className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={focusAreas.includes(area.key)}
                  onChange={() => toggleFocus(area.key)}
                />
                {area.label}
              </label>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">Press ⌘/Ctrl + Enter to ask</span>
          <button
            type="submit"
            disabled={isLoading || !question.trim()}
            className="bg-gray-800 text-white px-4 py-2 rounded font-semibold hover:bg-gray-700 disabled:opacity-50"
          >
            {isLoading ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </form>

      {!answer && !isLoading && !error && (
        <div className="mt-5 flex flex-wrap gap-2">
          {EXAMPLE_QUESTIONS.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => {
                setQuestion(example);
                ask(example);
              }}
              className="text-sm text-gray-700 bg-gray-100 hover:bg-yellow-100 rounded-full px-3 py-1.5"
            >
              {example}
            </button>
          ))}
        </div>
      )}

      {isLoading && (
        <div className="mt-6 flex items-center gap-2 text-gray-500">
          <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
          Querying the data…
        </div>
      )}

      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {answer && !isLoading && (
        <div className="mt-6">
          <div className="bg-white border border-gray-200 rounded shadow-sm px-5 py-4 text-gray-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {answer}
            </ReactMarkdown>
          </div>

          {queries.length > 0 && (
            <details className="mt-3">
              <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                SQL used ({queries.length})
              </summary>
              <div className="mt-2 space-y-2">
                {queries.map((sql, index) => (
                  <pre
                    key={index}
                    className="bg-gray-900 text-gray-100 text-xs rounded p-3 overflow-x-auto whitespace-pre-wrap"
                  >
                    {sql.trim()}
                  </pre>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default Assistant;
